import * as React from 'react';

import { cn } from '@/nucleo/utilitarios';

import { Spinner } from './Spinner';

/**
 * Botão "Entrar com gov.br", no padrão visual da Identidade Digital de
 * Governo (docs/CONECTOR-GOVBR.md, linha 37).
 *
 * O pictograma abaixo NÃO é ícone do Lucide — CLAUDE.md, "Regras de
 * interface", item 6, admite essa única exceção: marca de um provedor de
 * identidade externo não é ícone de interface, é identidade visual de
 * terceiro. É desenho próprio em SVG, feito à mão nesta sessão (nenhum
 * arquivo baixado da internet): um "check" estilizado dentro de um quadrado
 * de cantos arredondados, no espírito do selo oficial, sem reproduzir a arte
 * oficial. Usa `currentColor`, então acompanha `--govbr-foreground` — branco
 * no tema claro e no escuro, preto no modo de alto contraste.
 *
 * Segue o mesmo contrato dos demais componentes de `src/ui/`: encaminha
 * `ref`, aceita `className` e os atributos nativos de botão, e tem estado
 * desabilitado e estado de carregando.
 */
export interface BotaoGovBrProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Verdadeiro enquanto o redirecionamento para o gov.br está em curso. */
  carregando?: boolean;
}

export const BotaoGovBr = React.forwardRef<HTMLButtonElement, BotaoGovBrProps>(
  function BotaoGovBr({ className, carregando = false, disabled, children, ...props }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || carregando}
        aria-busy={carregando || undefined}
        className={cn(
          'inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-govbr text-sm font-semibold text-govbr-foreground transition-colors',
          'hover:bg-govbr/90',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {carregando ? (
          <Spinner size="sm" className="py-0" />
        ) : (
          <IconeGovBr className="h-5 w-5 shrink-0" aria-hidden="true" />
        )}
        <span>{children ?? 'Entrar com gov.br'}</span>
      </button>
    );
  },
);

/** Pictograma desenhado à mão — ver o comentário do componente acima. */
function IconeGovBr(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="1.5" y="1.5" width="21" height="21" rx="6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6.5 12.5L10 16L17.5 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
